import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Clock, X } from "lucide-react";
import { StorySources } from "../StorySources";
import { ImageCredit } from "../ImageCredit";
import { StoryFeedbackBar } from "../StoryFeedbackBar";
import { imageForStory } from "../categoryImages";
import { useArticleImage } from "../feed/useArticleImage";
import { timeAgo } from "../home/homeUtils";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { StoryLocationLabel } from "../StoryLocationLabel";
import { ConfidenceBadge } from "../atoms";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../../../utils/api";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

/* Content-type pill config for non-news KB cards */
const CONTENT_TYPE = {
  wikipedia:    { label: "WIKI",     cls: "bg-blue-50 text-blue-700 border-blue-200" },
  arxiv:        { label: "RESEARCH", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  pubmed:       { label: "STUDY",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  patent_uspto: { label: "PATENT",   cls: "bg-purple-50 text-purple-700 border-purple-200" },
  patent_epo:   { label: "PATENT",   cls: "bg-violet-50 text-violet-700 border-violet-200" },
};

/* ============================================================
   DNH — Highlights wall card.
   Props:
     story          — story object
     onOpen         — click handler
     dimmed         — lower opacity when another card is active
     imageAlreadySeen — true if this story's image_url already appeared
                        earlier in the wall → show category gradient instead
   ============================================================ */

// Category-specific gradient placeholders so every card looks distinct
const CAT_GRADIENTS = {
  politics:      "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
  business:      "linear-gradient(135deg, #0d1b2a 0%, #1b2838 50%, #1e3a5f 100%)",
  technology:    "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
  science:       "linear-gradient(135deg, #093028 0%, #237a57 100%)",
  health:        "linear-gradient(135deg, #1a1a2e 0%, #4a1942 100%)",
  sports:        "linear-gradient(135deg, #1c1c1c 0%, #4a0e0e 100%)",
  entertainment: "linear-gradient(135deg, #200122 0%, #6f0000 100%)",
  environment:   "linear-gradient(135deg, #0a2e0a 0%, #1a5c1a 100%)",
  default:       "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
};

const categoryGradient = (slug) =>
  CAT_GRADIENTS[(slug || "").split("-")[0]] || CAT_GRADIENTS.default;

export const HighlightCard = ({ story, dimmed, imageAlreadySeen, testIdPrefix = "highlight", from, topicBrief, searchFeedback }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const catLabel = (story.category_name || story.category_slug || "").replace(/-/g, " ");
  const imageUrl = story.image_url ? imageForStory({ image_url: story.image_url, category_slug: story.category_slug, id: story.id }, 640) : null;
  const image = useArticleImage(imageUrl);

  const hasRealImage = !imageAlreadySeen && image.available;
  const showGradient = !hasRealImage;
  const contentType = CONTENT_TYPE[story.kb_source];

  const openBrief = (event) => {
    if (event?.target?.closest?.("button, a, [role='dialog'], [data-card-interactive]")) return;
    navigate(topicBrief?.href || `/briefs/${encodeURIComponent(story.id)}`, { state: { from: from || { path: location.pathname + location.search, label: "stories" }, ...(topicBrief?.prepare && { prepare: true }), ...(!topicBrief && { generate: true }) } });
  };

  const handleDismiss = async (e) => {
    e.stopPropagation();
    if (!user) { toast.info("Sign in to dismiss stories from your feed"); return; }
    setDismissed(true);
    try {
      await apiFetch("/api/user/dismissed", {
        method: "POST",
        body: JSON.stringify({ story_id: story.id }),
      });
    } catch { /* hide is optimistic — keep card dismissed */ }
  };

  if (dismissed) return null;

  return (
    <motion.article
      layoutId={`${testIdPrefix}-card-${story.id}`}
      layout
      onClick={openBrief}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openBrief(); } }}
      role="link"
      tabIndex={0}
      aria-label={`Read brief: ${story.headline}`}
      data-testid={`${testIdPrefix}-card-${story.id}`}
      className="group cursor-pointer min-w-0 max-w-full rounded-lg border border-[var(--hairline)] bg-[var(--paper)] overflow-hidden flex flex-col hover:border-[var(--ink)] hover:shadow-[var(--shadow-soft)] transition-[border-color,box-shadow] duration-200"
      animate={{ opacity: dimmed ? 0 : 1, scale: dimmed ? 0.94 : 1 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      whileTap={{ scale: 0.98 }}
      style={{ pointerEvents: dimmed ? "none" : "auto" }}
    >
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
        {showGradient ? (
          /* Styled category gradient when no unique image is available */
          <div
            className="absolute inset-0"
            style={{ background: categoryGradient(story.category_slug) }}
            role="img"
            aria-label="No article image available"
            data-testid={`${testIdPrefix}-image-${story.id}-placeholder`}
          >
            {catLabel && (
              <div className="absolute inset-0 flex items-center justify-center px-6">
                <span className="font-serif text-[22px] leading-snug text-white/60 text-center capitalize">
                  {catLabel}
                </span>
              </div>
            )}
          </div>
        ) : (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={image.onError}
            data-testid={`${testIdPrefix}-image-${story.id}`}
          />
        )}

        {!topicBrief && <StoryLocationLabel story={story} preferState={location.pathname === "/today" || /^\/(country|state)\//.test(location.pathname)} testId={`${testIdPrefix}-origin-${story.id}`} className="absolute top-3 left-3 max-w-[calc(100%-52px)]" />}

        {/* Content-type pill for non-news KB cards (WIKI / RESEARCH / STUDY / PATENT) */}
        {contentType && (
          <span
            className={`absolute top-3 left-3 font-mono text-[8px] tracking-[0.1em] uppercase px-1.5 py-0.5 rounded border ${contentType.cls}`}
            data-testid={`${testIdPrefix}-content-type-${story.id}`}
          >
            {contentType.label}
          </span>
        )}

        {/* X dismiss button — top-right, same row as location pill */}
        <button
          type="button"
          data-testid={`${testIdPrefix}-dismiss-${story.id}`}
          aria-label="Dismiss story"
          title="Dismiss from feed"
          onClick={handleDismiss}
          className="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/40 text-white/80 hover:bg-black/65 hover:text-white transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
        >
          <X size={11} strokeWidth={2.5} />
        </button>

        {!showGradient && <ImageCredit story={story} light confidence={story.confidence} />}
      </div>

      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-mono text-[9px] uppercase text-[var(--crimson)]" data-testid={`${testIdPrefix}-category-${story.id}`}>{catLabel}</span>
          <span className="inline-flex items-center gap-1 font-mono text-[9px] text-[var(--muted)]" data-testid={`${testIdPrefix}-time-${story.id}`}><Clock size={10} />{timeAgo(story.event_latest_at || story.published_at)}</span>
        </div>
        <h3
          className="font-serif text-[20px] leading-[1.25] text-[var(--ink)] line-clamp-3 break-words"
          data-testid={`highlight-card-title-${story.id}`}
        >
          {story.headline}
        </h3>
        {story.summary && (
          <p className="font-sans text-[12.5px] leading-[1.5] text-[var(--muted)] line-clamp-3 mb-2" data-testid={`${testIdPrefix}-preview-${story.id}`}>
            {story.summary}
          </p>
        )}
        <div className="pt-1 mt-auto border-t border-[var(--hairline)] flex items-center justify-between gap-2 min-w-0">
          {topicBrief ? <span className="text-xs text-[var(--muted)]" data-testid={`${testIdPrefix}-sources-${story.id}`}>{topicBrief.publisher || `${topicBrief.sourceCount} sources · ${topicBrief.origins} reporting origins`}</span> : <StorySources story={story} publisherLabel />}
          {!topicBrief && story.confidence && <ConfidenceBadge level={story.confidence} className="shrink-0 !text-[9px] max-w-[88px]" />}
        </div>

        {topicBrief
          ? <div className="flex justify-between gap-2 items-center min-h-10 text-xs" data-testid={`${testIdPrefix}-open-${story.id}`}>
              <span>{topicBrief.label || "Read topic brief"}</span>
              <ArrowRight size={16} aria-hidden="true" />
            </div>
          : <StoryFeedbackBar
              story={story}
              showBookmark
              searchFeedback={searchFeedback || null}
            />
        }
      </div>
    </motion.article>
  );
};

export default HighlightCard;
